import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import type { PDFPageProxy, RenderTask } from "pdfjs-dist"
import * as React from "react"

import { cn } from "@/lib/utils"

import type { RemoteCursor } from "../api"
import type { Finding, FindingId, FindingSeverity, PointRect } from "../data/review-data"
import { getPdfDocument } from "./pdf-loader"

/**
 * The document surface for a review.
 *
 * Renders one page of the PDF via pdf.js and overlays finding boxes on top.
 *
 * Coordinate contract (matches backend `[x0,y0,x1,y1]` top-left origin):
 *   canvas backing-store size == pdf.js viewport size == CSS display size.
 * Overlay boxes convert PDF points to CSS pixels with the same scale ratio
 * pdf.js used to render, so they land pixel-exact on the text.
 *
 * DPR is not applied to the canvas. Multiplying by devicePixelRatio makes a
 * size/aspect mismatch between backing store and display, drifting every
 * overlay proportional to y. Sharper text is not worth misaligned findings.
 *
 * Scale posture (long docs with many pages):
 *   – Only the current page is decoded; PDFPageProxy.cleanup() releases the
 *     previous page's operator list before decoding a new one.
 *   – Every render is a real pdf.js RenderTask that we cancel() when the
 *     inputs change, so a fast page-flip never leaves an orphan rendering
 *     into the canvas.
 *   – The document cache is LRU-bounded (see pdf-loader.ts).
 */

export interface PageGeometry {
  /** Rendered canvas size in CSS pixels — matches pdf.js viewport. */
  cssWidth: number
  cssHeight: number
  /** The page's native size in PDF points — the divisor for bbox math. */
  pointWidth: number
  pointHeight: number
  zoom: number
  page: number
}

export function rectToStyle(rect: PointRect, geom: PageGeometry): React.CSSProperties {
  const scaleX = geom.cssWidth / geom.pointWidth
  const scaleY = geom.cssHeight / geom.pointHeight
  return {
    left: rect.x0 * scaleX,
    top: rect.y0 * scaleY,
    width: Math.max(3, (rect.x1 - rect.x0) * scaleX),
    height: Math.max(3, (rect.y1 - rect.y0) * scaleY),
  }
}

/**
 * Fixed hex colours — never theme tokens. This overlay sits on the
 * always-white PDF canvas, where a token that flips in dark mode would
 * vanish or swap severity meaning silently.
 */
const BOX_TONE: Record<FindingSeverity, string> = {
  error: "border-[#dc2626] bg-[#dc2626]/8",
  warning: "border-[#d97706] bg-[#d97706]/10",
  info: "border-[#2563eb] bg-[#2563eb]/10",
}

const BOX_TONE_ACTIVE: Record<FindingSeverity, string> = {
  error: "border-[#dc2626] bg-[#dc2626]/20 shadow-[0_0_0_4px_rgb(220_38_38/0.28)]",
  warning: "border-[#d97706] bg-[#d97706]/22 shadow-[0_0_0_4px_rgb(217_119_6/0.28)]",
  info: "border-[#2563eb] bg-[#2563eb]/22 shadow-[0_0_0_4px_rgb(37_99_235/0.28)]",
}

const BADGE_TONE: Record<FindingSeverity, string> = {
  error: "bg-[#111827] text-white ring-white/60",
  warning: "bg-[#111827] text-white ring-white/60",
  info: "bg-[#111827] text-white ring-white/60",
}

const BADGE_TONE_ACTIVE: Record<FindingSeverity, string> = {
  error: "bg-[#dc2626] text-white ring-white",
  warning: "bg-[#d97706] text-white ring-white",
  info: "bg-[#2563eb] text-white ring-white",
}

export interface PageSection {
  key: string
  label: string
  y0: number
  y1: number
}

export interface DrawResult {
  bbox: PointRect
  section: PageSection | null
}

/**
 * A finding-fill target: the framed bbox should occupy about this share of
 * the page's width, so a tiny label element gets enlarged, a large one only
 * modestly, and both leave enough surrounding context to judge them.
 */
const FOCUS_TARGET_FRACTION = 0.6
/**
 * Every focus click must produce a visible zoom bump — otherwise a wide
 * finding whose bbox already covers most of the page reads as "the button
 * did nothing but scroll". Guarantees at least this much magnification on
 * top of the current 1× baseline.
 */
const FOCUS_ZOOM_MIN = 1.5
/** Never zoom past this on a focus request — beyond it, context is lost. */
const FOCUS_ZOOM_MAX = 2.4

export interface FocusRequest {
  findingId: FindingId
  /** Bumped by the parent for each focus click, so re-focus on the same finding still fires. */
  nonce: number
}

export interface PdfSurfaceProps {
  documentUrl: string
  label: string
  subtitle?: string
  page: number
  zoom: number
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelectFinding: (id: FindingId) => void
  /**
   * A focus request from the parent. When set, PdfSurface computes the zoom
   * that fits the finding's bbox to a comfortable fraction of the viewport,
   * asks the parent to set that zoom, then — once the render has landed at
   * that zoom — scrolls the bbox to the middle of the viewport.
   */
  focusRequest?: FocusRequest | null
  /** Called when PdfSurface needs the parent to set a specific zoom. */
  onZoomChange?: (zoom: number) => void
  cursors?: RemoteCursor[]
  className?: string
  showTitle?: boolean
  drawMode?: boolean
  sections?: PageSection[]
  onDrawComplete?: (result: DrawResult) => void
  onDrawCancel?: () => void
}

/**
 * Scroll the given finding box to the middle of `scroller`.
 *
 * We deliberately do NOT use `element.scrollIntoView`: that walks up the DOM
 * and scrolls every scrollable ancestor to satisfy visibility, which in a
 * layout like ours (SplitPane → PDF pane → viewport → page) ends up moving
 * the outer pane, not the PDF viewport we care about. The bbox then never
 * enters the visible area of the PDF itself.
 *
 * Computing the offset ourselves keeps the scroll on the one container we
 * own and want to move — the `overflow-auto` div directly wrapping the
 * canvas — so the box lands in the middle of the reviewer's view.
 */
function centerBoxInScroller(scroller: HTMLElement | null, findingId: string): void {
  if (!scroller) return
  const box = scroller.querySelector<HTMLElement>(
    `[data-finding-box="${CSS.escape(findingId)}"]`,
  )
  if (!box) return
  const scRect = scroller.getBoundingClientRect()
  const bRect = box.getBoundingClientRect()
  const boxTopInContent = bRect.top - scRect.top + scroller.scrollTop
  const boxLeftInContent = bRect.left - scRect.left + scroller.scrollLeft
  const targetTop = boxTopInContent + bRect.height / 2 - scroller.clientHeight / 2
  const targetLeft = boxLeftInContent + bRect.width / 2 - scroller.clientWidth / 2
  scroller.scrollTo({
    top: Math.max(0, targetTop),
    left: Math.max(0, targetLeft),
    behavior: "smooth",
  })
}

export function PdfSurface({
  documentUrl,
  label,
  subtitle,
  page,
  zoom,
  findings,
  activeFindingId,
  onSelectFinding,
  focusRequest,
  onZoomChange,
  cursors = [],
  className,
  showTitle = true,
  drawMode = false,
  sections = [],
  onDrawComplete,
  onDrawCancel,
}: PdfSurfaceProps): JSX.Element {
  const wrapRef = React.useRef<HTMLDivElement>(null)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const pageRef = React.useRef<HTMLDivElement>(null)
  const [geometry, setGeometry] = React.useState<PageGeometry | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)

  React.useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      setContainerWidth(Math.max(rect.width - 48, 0))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Focus request from the parent — two-phase so the scroll never runs against
  // stale geometry. Phase 1: compute the fit-to-viewport zoom and ask the
  // parent for it. Phase 2: once `geometry.zoom` reflects that request, scroll
  // the bbox to the middle. Kept live until served (via nonce) so a request
  // that arrives mid page-turn resumes on its own.
  const servedNonceRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (!focusRequest || !geometry) return
    if (servedNonceRef.current === focusRequest.nonce) return
    const finding = findings.find((f) => f.id === focusRequest.findingId)
    if (!finding?.bbox) {
      servedNonceRef.current = focusRequest.nonce
      return
    }
    if (finding.bbox.page !== geometry.page) return // wait for the page to turn

    const widthFraction = Math.max(
      (finding.bbox.x1 - finding.bbox.x0) / geometry.pointWidth,
      0.02,
    )
    // Fit-to-viewport picks the zoom that makes the bbox occupy
    // ~FOCUS_TARGET_FRACTION of the page width. FOCUS_ZOOM_MIN is a floor:
    // even a bbox that already spans most of the page still magnifies on a
    // focus click, so the button never feels inert.
    const wanted = Math.max(
      FOCUS_ZOOM_MIN,
      Math.min(FOCUS_TARGET_FRACTION / widthFraction, FOCUS_ZOOM_MAX),
    )

    // Gate on the rendered zoom, never on the incoming `zoom` state. A single
    // request may take two effect runs: ask for the zoom, wait for a
    // geometry stamped with it, then scroll. Otherwise we scroll the old
    // layout — the box has often moved off screen at the new zoom and the
    // request would appear to "do nothing".
    if (Math.abs(geometry.zoom - wanted) > 0.001) {
      onZoomChange?.(wanted)
      return
    }
    const frame = requestAnimationFrame(() => {
      centerBoxInScroller(scrollRef.current, finding.id)
      servedNonceRef.current = focusRequest.nonce
    })
    return () => cancelAnimationFrame(frame)
  }, [focusRequest, geometry, findings, onZoomChange])

  // Selection scroll (not a focus): if the reviewer clicks a finding on the
  // current page, keep its bbox in view — but do NOT bump the zoom. Skips
  // while a focus request is unserved (the focus effect owns the scroll then).
  React.useEffect(() => {
    if (!geometry || !activeFindingId) return
    if (focusRequest && servedNonceRef.current !== focusRequest.nonce) return
    centerBoxInScroller(scrollRef.current, activeFindingId)
  }, [activeFindingId, geometry, focusRequest])

  React.useEffect(() => {
    if (!documentUrl || containerWidth <= 0) return

    let cancelled = false
    let renderTask: RenderTask | null = null
    let renderedPage: PDFPageProxy | null = null
    setError(null)

    void (async () => {
      try {
        const doc = await getPdfDocument(documentUrl)
        if (cancelled) return
        const pdfPage = await doc.getPage(page)
        if (cancelled) return
        renderedPage = pdfPage

        const base = pdfPage.getViewport({ scale: 1 })
        const baseWidth = Math.max(320, Math.min(containerWidth, 1120))
        const targetWidth = baseWidth * zoom
        const viewport = pdfPage.getViewport({ scale: targetWidth / base.width })

        const canvas = canvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx) return

        // Reallocating the canvas backing store is what discards the old
        // frame — do it before starting the new render so the reviewer never
        // sees stale pixels flash from a cancelled prior task.
        canvas.width = viewport.width
        canvas.height = viewport.height

        renderTask = pdfPage.render({ canvas, canvasContext: ctx, viewport })
        await renderTask.promise
        if (cancelled) return

        setGeometry({
          cssWidth: viewport.width,
          cssHeight: viewport.height,
          pointWidth: base.width,
          pointHeight: base.height,
          zoom,
          page,
        })
      } catch (cause) {
        if (cancelled) return
        // pdf.js throws its own RenderingCancelledException when render()
        // is aborted; that is not an error to surface to the reviewer.
        if (cause instanceof Error && cause.name === "RenderingCancelledException") return
        setError(cause instanceof Error ? cause.message : "Could not render the PDF.")
      }
    })()

    return () => {
      cancelled = true
      // Cancel the outstanding render so a fast page-flip doesn't waste
      // CPU rendering a page nobody will see, and so the next render can
      // own the canvas without contending for it.
      renderTask?.cancel()
      // Free the previous page's operator list, glyph paths and worker
      // buffers. Without this, jumping around a long doc grows the worker
      // heap for every page visited.
      renderedPage?.cleanup()
    }
  }, [documentUrl, page, zoom, containerWidth])

  return (
    <div
      ref={wrapRef}
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-[color:var(--canvas)]",
        className,
      )}
    >
      {/* Radix ScrollArea gives us ONE guaranteed scrollable ancestor —
          the [data-radix-scroll-area-viewport] node inside the Root. Every
          other layer above is overflow: hidden, so scrollIntoView (or a
          manual scrollTo) unambiguously moves this viewport and only this
          viewport. Legacy uses the exact same primitive for the same
          reason: it took two rewrites of my own to accept that this is not
          a place to be clever. */}
      <ScrollAreaPrimitive.Root
        type="hover"
        className="relative flex-1 overflow-hidden"
      >
        <ScrollAreaPrimitive.Viewport
          ref={scrollRef}
          className="h-full w-full"
        >
          <div className="flex min-h-full flex-col items-center px-6 pt-4 pb-6">
            {showTitle && geometry && (
              <div
                className="mb-2 flex items-baseline justify-between gap-3"
                style={{ width: geometry.cssWidth }}
              >
                <div className="min-w-0 flex items-baseline gap-2">
                  <p className="truncate text-body-sm font-medium text-text">{label}</p>
                  {subtitle && (
                    <>
                      <span aria-hidden="true" className="text-text-3">
                        ·
                      </span>
                      <p className="truncate text-caption text-text-3">{subtitle}</p>
                    </>
                  )}
                </div>
                <span className="shrink-0 font-id text-caption tabular-nums text-text-3">
                  Page {page}
                </span>
              </div>
            )}
            <div
              ref={pageRef}
              className="relative shrink-0 bg-white shadow-sm ring-1 ring-[color:var(--canvas-page-ring)]"
              style={{ width: geometry?.cssWidth ?? "100%" }}
            >
              <canvas
                ref={canvasRef}
                className={cn("block", error && "hidden")}
                aria-hidden="true"
              />
              {error && (
                <div
                  role="alert"
                  className="flex h-[220px] items-center justify-center px-6 text-center text-body-sm text-err"
                >
                  {error}
                </div>
              )}
              {geometry && (
                <>
                  <Overlay
                    geom={geometry}
                    findings={findings}
                    activeFindingId={activeFindingId}
                    onSelect={onSelectFinding}
                  />
                  <CursorLayer geom={geometry} cursors={cursors} />
                  {drawMode && (
                    <DrawLayer
                      geom={geometry}
                      sections={sections}
                      onComplete={(r) => onDrawComplete?.(r)}
                      onCancel={() => onDrawCancel?.()}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </ScrollAreaPrimitive.Viewport>
        <ScrollAreaPrimitive.Scrollbar
          orientation="vertical"
          className="flex h-full w-2.5 touch-none select-none border-l border-l-transparent p-[1px]"
        >
          <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-border" />
        </ScrollAreaPrimitive.Scrollbar>
        <ScrollAreaPrimitive.Scrollbar
          orientation="horizontal"
          className="flex h-2.5 w-full touch-none select-none flex-col border-t border-t-transparent p-[1px]"
        >
          <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-border" />
        </ScrollAreaPrimitive.Scrollbar>
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    </div>
  )
}

function DrawLayer({
  geom,
  sections,
  onComplete,
  onCancel,
}: {
  geom: PageGeometry
  sections: PageSection[]
  onComplete: (r: DrawResult) => void
  onCancel: () => void
}): JSX.Element {
  const [drag, setDrag] = React.useState<{
    x0: number
    y0: number
    x1: number
    y1: number
  } | null>(null)
  const layerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setDrag(null)
        onCancel()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onCancel])

  const localPoint = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = layerRef.current!.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(rect.width, e.clientX - rect.left)),
      y: Math.max(0, Math.min(rect.height, e.clientY - rect.top)),
    }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const { x, y } = localPoint(e)
    setDrag({ x0: x, y0: y, x1: x, y1: y })
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return
    const { x, y } = localPoint(e)
    setDrag((d) => (d ? { ...d, x1: x, y1: y } : d))
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    const scaleX = geom.cssWidth / geom.pointWidth
    const scaleY = geom.cssHeight / geom.pointHeight
    const x0 = Math.min(drag.x0, drag.x1) / scaleX
    const y0 = Math.min(drag.y0, drag.y1) / scaleY
    const x1 = Math.max(drag.x0, drag.x1) / scaleX
    const y1 = Math.max(drag.y0, drag.y1) / scaleY
    setDrag(null)
    if (x1 - x0 < 8 || y1 - y0 < 8) return
    const yMid = (y0 + y1) / 2
    const section = sections.find((s) => yMid >= s.y0 && yMid < s.y1) ?? null
    onComplete({
      bbox: { page: geom.page, x0, y0, x1, y1 },
      section,
    })
  }

  const box = drag
    ? {
        left: Math.min(drag.x0, drag.x1),
        top: Math.min(drag.y0, drag.y1),
        width: Math.abs(drag.x1 - drag.x0),
        height: Math.abs(drag.y1 - drag.y0),
      }
    : null

  return (
    <div
      ref={layerRef}
      role="application"
      aria-label="Draw a box to add a finding"
      className="absolute inset-0 cursor-crosshair"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {box && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-[2px] border-[1.5px] border-dashed border-[color:var(--brand)] bg-[color:var(--brand)]/10"
          style={box}
        >
          {box.width > 40 && box.height > 20 && (
            <span className="absolute -bottom-5 right-0 rounded-sm bg-[color:var(--brand)] px-1.5 py-0.5 font-id text-[9.5px] font-medium tabular-nums text-white shadow-sm">
              {Math.round(box.width / (geom.cssWidth / geom.pointWidth))}×
              {Math.round(box.height / (geom.cssHeight / geom.pointHeight))} pt
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const Overlay = React.memo(function Overlay({
  geom,
  findings,
  activeFindingId,
  onSelect,
}: {
  geom: PageGeometry
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelect: (id: FindingId) => void
}): JSX.Element {
  // Prefilter once per findings/page change. On a long label with many
  // findings, doing this inline in render walks the entire array on every
  // cursor tick and every hover.
  const boxes = React.useMemo(
    () =>
      findings.filter(
        (f) => f.placement === "box" && f.bbox && f.bbox.page === geom.page,
      ),
    [findings, geom.page],
  )
  return (
    <div className="pointer-events-none absolute inset-0">
      {boxes.map((f) => {
        if (!f.bbox) return null
        const style = rectToStyle(f.bbox, geom)
        const active = activeFindingId === f.id
        return (
          <button
            key={f.id}
            type="button"
            data-finding-box={f.id}
            onClick={() => onSelect(f.id)}
            aria-label={`Finding ${f.number}: ${f.claim}`}
            title={`Finding ${f.number} · ${f.ruleId} — ${f.claim}`}
            className={cn(
              "pointer-events-auto absolute rounded-[2px] border-2 transition-all duration-150 ease-out",
              "hover:bg-opacity-30 hover:z-10",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--brand)]",
              active ? BOX_TONE_ACTIVE[f.severity] : BOX_TONE[f.severity],
              active && "z-20 animate-[findingPulse_900ms_ease-out_1]",
            )}
            style={style}
          >
            {/* Badge in the left gutter, vertically centered. Label lines
                are ~14pt apart — a badge inside or above the box lands on
                the line before and obscures the very words it annotates. */}
            <span
              className={cn(
                "absolute -left-1 top-1/2 grid h-4 min-w-4 -translate-x-full -translate-y-1/2 place-items-center rounded-full px-1 font-id text-[10px] font-semibold leading-none tabular-nums ring-1 shadow-sm",
                active ? BADGE_TONE_ACTIVE[f.severity] : BADGE_TONE[f.severity],
              )}
              aria-hidden="true"
            >
              {f.number}
            </span>
          </button>
        )
      })}
    </div>
  )
})

const CursorLayer = React.memo(function CursorLayer({
  geom,
  cursors,
}: {
  geom: PageGeometry
  cursors: RemoteCursor[]
}): JSX.Element | null {
  // Filter to current page once per cursor batch. Presence updates arrive
  // as often as reviewers move their mouse; running .filter() on every
  // parent re-render is unbounded churn.
  const onPage = React.useMemo(
    () => cursors.filter((c) => c.page === geom.page),
    [cursors, geom.page],
  )
  if (onPage.length === 0) return null
  const scaleX = geom.cssWidth / geom.pointWidth
  const scaleY = geom.cssHeight / geom.pointHeight
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {onPage.map((c) => (
        <div
          key={c.userId}
          className="absolute transition-[left,top] duration-100 ease-out"
          style={{ left: c.x * scaleX, top: c.y * scaleY }}
        >
          <svg
            className="drop-shadow-[0_2px_4px_rgb(0_0_0/0.4)]"
            width={14}
            height={16}
            viewBox="0 0 14 16"
            fill="var(--brand)"
          >
            <path d="M0 0 L14 6 L6 8 L4 16 Z" />
          </svg>
          <span className="ml-2 inline-block rounded-sm bg-[color:var(--brand)] px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm">
            {c.role}
          </span>
        </div>
      ))}
    </div>
  )
})
